// src/pages/masters/houseMaster/HouseMasterBulkImportPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  UploadCloud,
  CheckCircle,
  XCircle,
  FileText,
  Download,
} from "react-feather";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  bulkImportHouseThunk,
  clearImportState,
} from "../../../store/slices/houseMasterSlice";

import "./HouseMasterPage.css";

// ---------------------------
// helpers
// ---------------------------
const toStr = (v) => String(v ?? "").trim();

const yesNo = (v) => {
  const s = String(v ?? "").toLowerCase().trim();
  return ["yes", "y", "true", "1"].includes(s) ? "yes" : "no";
};

// ✅ Excel date: supports dd/mm/yyyy, dd-mm-yyyy, yyyy-mm-dd, serial numbers
const toISODate = (v) => {
  if (v == null || v === "") return "";
  if (typeof v === "number" && Number.isFinite(v)) {
    const d = XLSX.SSF.parse_date_code(v);
    if (!d) return "";
    const yyyy = String(d.y).padStart(4, "0");
    const mm = String(d.m).padStart(2, "0");
    const dd = String(d.d).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
  const s = toStr(v);
  if (!s) return "";

  const m1 = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (m1)
    return `${m1[3]}-${String(m1[2]).padStart(2, "0")}-${String(m1[1]).padStart(
      2,
      "0"
    )}`;

  const m2 = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m2) return s;

  return s;
};

// ---------------------------
// TEMPLATE HEADERS (MATCH YOUR EXCEL FORMAT)
// ✅ same header must show in download template + preview table
// ---------------------------
const TEMPLATE_HEADERS = [
  "QR CODE",
  "WARD ID",
  "FAMILY HEAD",
  "DOOR NO",
  "MOBILE NO",
  "WATER CONNECTION",
  "SLRM COLLECTION",
  "METER TYPE", // optional
  "SLRM TYPE", // optional
  "LAST READING",
  "LAST READING DATE",
  "WATER BILL BALANCE",
  "SLRM BILL BALANCE",

  // ✅ NEW
  "SLRM LAST BILL DATE",
];

const downloadHouseImportTemplate = () => {
  const aoa = [
    TEMPLATE_HEADERS,
    [
      "QR0001", // QR CODE
      1, // WARD ID
      "Ramesh", // FAMILY HEAD
      "12-A", // DOOR NO
      "9876543210", // MOBILE NO
      "yes", // WATER CONNECTION
      "no", // SLRM COLLECTION
      "1", // METER TYPE (optional)
      1, // SLRM TYPE (optional)
      "", // LAST READING
      "2025-01-11", // LAST READING DATE (YYYY-MM-DD)
      120, // WATER BILL BALANCE
      "", // SLRM BILL BALANCE

      // ✅ NEW
      "2025-02-05", // SLRM LAST BILL DATE (YYYY-MM-DD)
    ],
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!freeze"] = { xSplit: 0, ySplit: 1 };
  ws["!cols"] = TEMPLATE_HEADERS.map(() => ({ wch: 18 }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "HouseMaster");
  XLSX.writeFile(wb, "House_Master_Import_Template.xlsx");
};

// ---------------------------
// find header row (supports random top rows in excel)
// ---------------------------
const findHeaderRowIndex = (sheet) => {
  const range = XLSX.utils.decode_range(sheet["!ref"] || "A1:A1");
  const NEEDLES = [
    "DOOR",
    "WARD",
    "FAMILY",
    "MOBILE",
    "QR",
    "METER",
    "WATER",
    "SLRM",
    "LAST",
  ];

  for (let r = range.s.r; r <= Math.min(range.e.r, range.s.r + 50); r++) {
    let hit = 0;

    for (let c = range.s.c; c <= Math.min(range.e.c, range.s.c + 30); c++) {
      const addr = XLSX.utils.encode_cell({ r, c });
      const v = String(sheet[addr]?.v ?? "").toUpperCase().trim();
      if (!v) continue;
      if (NEEDLES.some((n) => v.includes(n))) hit++;
    }

    if (hit >= 2) return r;
  }

  return range.s.r;
};

// ---------------------------
// validation (PREVIEW ONLY)
// ✅ QR CODE unique will be checked by backend
// ✅ Meter type & slrm type are NOT mandatory
// ✅ door_no NOT required now
// ---------------------------
const validatePreviewRow = (row) => {
  const qr = row["QR CODE"] ?? row["qr_code"] ?? "";
  const ward = row["WARD ID"] ?? row["ward_id"] ?? row["WardId"] ?? row["ward"] ?? "";

  const lastDate =
    row["LAST READING DATE"] ??
    row["LAST BILL DATE"] ??
    row["last_bill_date"] ??
    "";

  const slrmLastBillDate =
    row["SLRM LAST BILL DATE"] ??
    row["SLRM LAST READING DATE"] ??
    row["slrm_last_bill_date"] ??
    "";

  const errs = [];

  if (!toStr(qr)) errs.push("qr_code required");
  if (!toStr(ward)) errs.push("ward_id required");

  if (toStr(lastDate)) {
    const iso = toISODate(lastDate);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) errs.push("last_reading_date invalid");
  }

  if (toStr(slrmLastBillDate)) {
    const iso = toISODate(slrmLastBillDate);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) errs.push("slrm_last_bill_date invalid");
  }

  return errs;
};

export default function HouseMasterBulkImportPage() {
  const dispatch = useDispatch();

  const importing = useSelector((s) => s.houseMaster.importing);
  const importError = useSelector((s) => s.houseMaster.importError);
  const importResult = useSelector((s) => s.houseMaster.importResult);

  const [fileName, setFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [firstSheetName, setFirstSheetName] = useState("");

  // store RAW rows (all excel columns)
  const [rawExcelRows, setRawExcelRows] = useState([]);

  // ✅ preview columns must be in SAME ORDER like template headers
  const columns = useMemo(() => {
    const ordered = [...TEMPLATE_HEADERS];

    // add any extra columns that exist in excel but not in template
    const extra = new Set();
    (rawExcelRows || []).forEach((r) => {
      Object.keys(r || {}).forEach((k) => {
        const kk = toStr(k);
        if (kk && !ordered.includes(kk)) extra.add(kk);
      });
    });

    return [...ordered, ...Array.from(extra)];
  }, [rawExcelRows]);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const rows = useMemo(() => {
    return (rawExcelRows || []).map((r, i) => {
      const errors = validatePreviewRow(r);
      return { ...r, __row: i + 1, __errors: errors, __ok: errors.length === 0 };
    });
  }, [rawExcelRows]);

  const stats = useMemo(() => {
    const total = rows.length;
    const ok = rows.filter((r) => r.__ok).length;
    const bad = total - ok;
    return { total, ok, bad };
  }, [rows]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(rows.length / pageSize));
  }, [rows.length, pageSize]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [fileName, pageSize]);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    setSelectedFile(f);
    dispatch(clearImportState());

    setFileName(f.name);
    setRawExcelRows([]);
    setFirstSheetName("");

    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });

      const sheetName = wb.SheetNames?.[0];
      if (!sheetName) {
        toast.error("No sheets found");
        return;
      }

      setFirstSheetName(sheetName);

      const sheet = wb.Sheets[sheetName];
      if (!sheet || !sheet["!ref"]) {
        toast.error(`No data in sheet: ${sheetName}`);
        return;
      }

      const headerRow = findHeaderRowIndex(sheet);

      // RAW rows with ALL columns
      const json = XLSX.utils.sheet_to_json(sheet, {
        defval: "",
        range: headerRow,
        raw: true,
      });

      if (!json?.length) {
        toast.error("No readable rows (check header row / merged cells)");
        return;
      }

      setRawExcelRows(json);
      toast.success(`Loaded ${json.length} rows from sheet: ${sheetName}`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to read Excel file");
    }
  };

  const clearAll = () => {
    setSelectedFile(null);
    setFileName("");
    setRawExcelRows([]);
    setFirstSheetName("");
    setPage(1);
    setPageSize(50);
    dispatch(clearImportState());
  };

  const onImport = async () => {
    if (!selectedFile) return toast.warn("Please choose an Excel file");

    const r = await dispatch(
      bulkImportHouseThunk({
        file: selectedFile,
      })
    );

    if (bulkImportHouseThunk.fulfilled.match(r)) {
      toast.success(r.payload?.message || "Imported successfully");
    } else {
      toast.error(r.payload || "Import failed");
    }
  };

  const skippedErrors = Array.isArray(importResult?.errors)
    ? importResult.errors
    : [];

  return (
    <div className="container-fluid px-3 py-4 hm-page">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h4 className="mb-1 fw-bold hm-title">House Master Bulk Import</h4>
          {firstSheetName ? (
            <div className="text-muted small">
              Using first sheet: <b>{firstSheetName}</b>
            </div>
          ) : null}
        </div>

        <div className="d-flex gap-2 flex-wrap">
          <Button
            variant="outline-success"
            onClick={downloadHouseImportTemplate}
            disabled={importing}
          >
            <Download size={16} className="me-2" />
            Download Template
          </Button>

          <Button variant="light" onClick={clearAll} disabled={importing}>
            Clear
          </Button>

          <Button
            onClick={onImport}
            disabled={importing || !selectedFile}
            className="hm-primary-btn"
            style={{ background: "#2563eb", borderColor: "#2563eb" }}
          >
            {importing ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Importing...
              </>
            ) : (
              <>
                <UploadCloud size={16} className="me-2" />
                Import First Sheet
              </>
            )}
          </Button>
        </div>
      </div>

      {importError ? (
        <Alert variant="danger" className="hm-card-soft">
          <div className="fw-bold">Import Error</div>
          <div>{importError}</div>
        </Alert>
      ) : null}

      {importResult ? (
        <Alert variant="success" className="hm-card-soft">
          <div className="fw-bold">Import Result</div>
          <div className="mt-1">
            {importResult.message || "Done"}
            {importResult.inserted != null ? (
              <>
                {" "}
                | Inserted: <b>{importResult.inserted}</b>
              </>
            ) : null}
            {importResult.skipped != null ? (
              <>
                {" "}
                | Skipped: <b>{importResult.skipped}</b>
              </>
            ) : null}
          </div>
        </Alert>
      ) : null}

      {/* skipped rows from backend */}
      {skippedErrors.length > 0 ? (
        <Card className="hm-card-soft mb-3">
          <Card.Body>
            <div className="fw-bold mb-2">
              Skipped Records ({skippedErrors.length})
            </div>
            <div className="hm-table-wrap">
              <Table hover className="hm-table mb-0">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Reason</th>
                    <th>Ward ID</th>
                    <th>Door No</th>
                    <th>Family Head</th>
                    <th>Mobile</th>
                    <th>QR CODE</th>
                  </tr>
                </thead>
                <tbody>
                  {skippedErrors.slice(0, 200).map((e, idx) => {
                    const d = e?.data || {};
                    return (
                      <tr key={idx}>
                        <td className="text-muted fw-semibold">{e?.row ?? "-"}</td>
                        <td>
                          <Badge bg="danger">{e?.message || "Skipped"}</Badge>
                        </td>
                        <td>{d.ward_id || "-"}</td>
                        <td>{d.door_no || "-"}</td>
                        <td>{d.family_head || "-"}</td>
                        <td>{d.mobile || "-"}</td>
                        <td>{d.qr_code || "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      ) : null}

      <Row className="g-3">
        <Col md={12}>
          <Card className="hm-card-soft">
            <Card.Body>
              <Form.Group>
                <Form.Label className="fw-bold">Upload Excel</Form.Label>
                <Form.Control
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={onPickFile}
                  disabled={importing}
                />

                {fileName ? (
                  <div className="text-muted small mt-2 d-flex align-items-center gap-2">
                    <FileText size={14} /> {fileName}
                  </div>
                ) : null}
              </Form.Group>

              <hr />

              <div className="d-flex gap-3 flex-wrap">
                <Badge bg="secondary">Total: {stats.total}</Badge>
                <Badge bg="success">Valid: {stats.ok}</Badge>
                <Badge bg="danger">Errors: {stats.bad}</Badge>
              </div>

              {columns.length > 0 ? (
                <div className="text-muted small mt-2">
                  Showing <b>{columns.length}</b> columns from Excel.
                </div>
              ) : null}
            </Card.Body>
          </Card>
        </Col>

        {/* ✅ ALL EXCEL DATA TABLE */}
        <Col md={12}>
          <Card className="hm-card">
            <Card.Body className="p-0">
              <div className="hm-table-wrap">
                <Table hover className="hm-table mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 70 }}>#</th>
                      {columns.map((c) => (
                        <th key={c}>{c}</th>
                      ))}
                      <th style={{ width: 220 }}>Validation</th>
                    </tr>
                  </thead>

                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={columns.length + 2}
                          className="text-center py-4 text-muted"
                        >
                          Upload a file to preview the first sheet (all columns)
                        </td>
                      </tr>
                    ) : (
                      pagedRows.map((r) => (
                        <tr key={r.__row}>
                          <td className="text-muted fw-semibold">{r.__row}</td>

                          {columns.map((c) => (
                            <td key={c}>
                              {r[c] === "" || r[c] == null
                                ? "—"
                                : c === "LAST READING DATE" || c === "SLRM LAST BILL DATE"
                                ? toISODate(r[c]) || "—"
                                : String(r[c])}
                            </td>
                          ))}

                          <td>
                            {r.__ok ? (
                              <Badge bg="success">
                                <CheckCircle size={14} className="me-1" />
                                OK
                              </Badge>
                            ) : (
                              <Badge bg="danger">
                                <XCircle size={14} className="me-1" />
                                {r.__errors.join(", ")}
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {rows.length > 0 ? (
                <div className="d-flex justify-content-between align-items-center p-2 border-top flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="text-muted small">Rows per page</span>
                    <Form.Select
                      size="sm"
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                      style={{ width: 120 }}
                    >
                      {[25, 50, 100, 200].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </Form.Select>

                    <span className="text-muted small">
                      Showing {(page - 1) * pageSize + 1} -{" "}
                      {Math.min(page * pageSize, rows.length)} of {rows.length}
                    </span>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <Button
                      size="sm"
                      variant="light"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Prev
                    </Button>

                    <span className="small">
                      Page <b>{page}</b> / {totalPages}
                    </span>

                    <Button
                      size="sm"
                      variant="light"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              ) : null}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}